import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { computed, observable, makeAutoObservable } from 'mobx';
import { CustomDropdown, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { ButtonWrapper, ButtonTag } from 'src/components/RealityCheckForm/RealityCheckTab.style';
import { DateTime } from 'src_common/utils/time/time';
import { SdkStaff, StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { useAppStateContext } from 'src/AppState/AppState';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { OpenapiProxyStaffGetRealityCheckResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_get_reality_check';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

export const REALITY_CHECK_OPTIONS = {
    30: '30 minutes',
    60: '1 hour',
    90: '90 minutes',
    120: '2 hours',
    180: '3 hours',
    300: '5 hours',
};
export const REALITY_CHECK_LIMITED_OPTIONS = {
    30: '30 minutes',
    60: '1 hour',
    90: '90 minutes',
};

export interface RealityCheckOptionsType {
    realityCheckFrequency: number | null;
    realityCheckFrequencyDateSet: string | null;
    realityCheckFrequencyRequestDate: string | null;
    requestedRealityCheckFrequency: number | null;
}

class RealityCheckState {
    @observable public selectedValue: string | null = null;
    public readonly realityCheckResource: Resource<OpenapiProxyStaffGetRealityCheckResponse200Type>;

    public constructor(
        private readonly customerId: number,
        private readonly sdkStaff: SdkStaff,
        private readonly permissionRealityCheck: boolean,
        private readonly models: ModelsState,
        private readonly isLimitedType: boolean
    ) {
        this.realityCheckResource = new Resource(async (): Promise<OpenapiProxyStaffGetRealityCheckResponse200Type> => {
            return await this.sdkStaff.getRealityCheck({ customer_id: this.customerId });
        });
        makeAutoObservable(this);
    }
    @computed private get basicDataUserModel(): StaffAccountBasicDataType | null {
        return this.models.getUserModel(this.customerId).basicData.data;
    }

    @computed public get pendingActivableAt(): string | null {
        const realityCheck = this.realityCheckResource.get();
        if (realityCheck.type !== 'ready') {
            return null;
        }

        const activableAt = realityCheck.value.pending?.activableAt;

        if (activableAt === null || activableAt === undefined) {
            return null;
        }

        return DateTime.from(activableAt)?.format('DD/MM/YYYY HH:mm:ss') ?? null;
    }

    @computed public get realityCheckOptions(): RealityCheckOptionsType {
        const userData = this.basicDataUserModel;

        return {
            realityCheckFrequency: userData?.realityCheckFrequency ?? null,
            realityCheckFrequencyDateSet: userData?.realityCheckFrequencyDateSet ?? null,
            realityCheckFrequencyRequestDate: userData?.realityCheckFrequencyRequestDate ?? null,
            requestedRealityCheckFrequency: userData?.requestedRealityCheckFrequency ?? null,
        };
    }

    public realityCheckOptionsString = (realityCheckFrequency: number | null): string => {
        if (realityCheckFrequency === null) {
            return 'none';
        }
        const realityCheckOption = this.REALITY_CHECK_OPTIONS_ARRAY().find(
            (item) => item.value === realityCheckFrequency.toString()
        );
        return realityCheckOption?.label ?? 'none';
    };

    public onSubmit = async (): Promise<void> => {
        if (this.permissionRealityCheck === true || this.selectedValue === null) {
            return;
        }

        const isRealityCheckSet = this.realityCheckOptions.realityCheckFrequency !== null;
        const status = isRealityCheckSet === true ? 'pending' : 'active';

        await this.onSetRealityCheck(parseInt(this.selectedValue), status);
    };

    public onUpdate = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            this.selectedValue = changedValue;
        }
    };

    public REALITY_CHECK_OPTIONS_ARRAY = (): Array<{ value: string; label: string }> => {
        const out = [];
        const optionsForRealityCheck = this.isLimitedType ? REALITY_CHECK_LIMITED_OPTIONS : REALITY_CHECK_OPTIONS;

        for (const [key, value] of Object.entries(optionsForRealityCheck)) {
            out.push({
                value: key,
                label: value,
            });
        }

        return out;
    };

    private onSetRealityCheck = async (duration: number, status: 'active' | 'pending'): Promise<void> => {
        try {
            await this.sdkStaff.setRealityCheck({
                customer_id: this.customerId,
                status,
                requestBody: {
                    duration,
                },
            });

            await this.realityCheckResource.refresh();
        } catch (e) {
            console.log(e);
        }
    };
}

interface RealityCheckTabPropsType {
    customerId: number;
    permissionRealityCheck: boolean;
}

export const RealityCheckTab = observer(
    'DepositLimitTab',
    ({ customerId, permissionRealityCheck }: RealityCheckTabPropsType) => {
        const { sdkStaff, models, common } = useAppStateContext();
        const {
            config: { realityCheckLimitedType },
        } = ConfigState.get(common);
        const [state] = React.useState(
            () => new RealityCheckState(customerId, sdkStaff, permissionRealityCheck, models, realityCheckLimitedType)
        );

        return (
            <div className='row'>
                <div className='col-4'>
                    <h2
                        className='header'
                        data-test='reality-check-status-header'
                    >
                        Reality Check Status:
                    </h2>
                    <table>
                        <tbody>
                            <tr data-test='active-reality-check-row'>
                                <th>Active:</th>
                                <td>
                                    {state.realityCheckOptionsString(state.realityCheckOptions.realityCheckFrequency)}
                                </td>
                            </tr>
                            <tr data-test='pending-reality-check-row'>
                                <th>Pending:</th>
                                <td>
                                    {state.realityCheckOptionsString(
                                        state.realityCheckOptions.requestedRealityCheckFrequency
                                    )}
                                    &nbsp;
                                    {state.realityCheckResource.getReady()?.pending?.activableAt !== null &&
                                    state.realityCheckResource.getReady()?.pending?.activableAt !== undefined
                                        ? `at ${state.pendingActivableAt}`
                                        : undefined}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div
                        className='reality-check-form'
                        data-test='reality-check-form'
                    >
                        <CustomDropdown
                            optionsList={state.REALITY_CHECK_OPTIONS_ARRAY}
                            selectedValue={(): string | null => state.selectedValue}
                            onChangeCallback={state.onUpdate}
                            placeholder='Set frequency'
                            labelHeader='Reality Check frequency'
                        />
                        <ButtonWrapper
                            isDisabled={permissionRealityCheck}
                            className='buttons'
                        >
                            <ButtonTag
                                disabled={permissionRealityCheck}
                                onClick={state.onSubmit}
                                className='btn btn-primary'
                                type='submit'
                                data-test='set-reality-check-button'
                            >
                                Set reality check
                            </ButtonTag>
                        </ButtonWrapper>
                    </div>
                </div>
            </div>
        );
    }
);
